import { useDeferredValue, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pin, Search } from 'lucide-react';
import { ActionMenu, Button, Spinner } from '@/shared/ui';
import { assistenteService, type ConversaMia } from './assistenteService';
import styles from './HistoricoMia.module.css';

type Props = {
  idAtual: number | null;
  onAbrir: (conversa: ConversaMia) => void;
  onVoltar: () => void;
  /** Arquivar a conversa aberta volta para uma conversa em branco. */
  onArquivouAtual: () => void;
};

export const CHAVE_CONVERSAS = ['assistente', 'conversas'] as const;

function quando(iso: string | null): string {
  if (!iso) return '';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '';
  const hoje = new Date();
  if (data.toDateString() === hoje.toDateString()) {
    return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function HistoricoMia({ idAtual, onAbrir, onVoltar, onArquivouAtual }: Props) {
  const queryClient = useQueryClient();
  const [busca, setBusca] = useState('');
  const termo = useDeferredValue(busca.trim());
  const [renomeando, setRenomeando] = useState<{ id: number; titulo: string } | null>(null);

  const conversas = useQuery({
    queryKey: [...CHAVE_CONVERSAS, termo],
    queryFn: () => assistenteService.conversas(termo),
    staleTime: 15_000,
  });

  const atualizar = useMutation({
    mutationFn: ({ id, mudanca }: { id: number; mudanca: { titulo?: string; fixada?: boolean; arquivada?: boolean } }) =>
      assistenteService.atualizarConversa(id, mudanca),
    onSuccess: (_conversa, { id, mudanca }) => {
      void queryClient.invalidateQueries({ queryKey: CHAVE_CONVERSAS });
      if (mudanca.arquivada && id === idAtual) onArquivouAtual();
    },
  });

  return (
    <div className={styles.historico}>
      <div className={styles.topo}>
        <Button variant="ghost" size="sm" aria-label="Voltar para a conversa" onClick={onVoltar}>
          <ArrowLeft size={18} />
        </Button>
        <strong>Conversas</strong>
      </div>
      <label className={styles.busca}>
        <Search size={16} aria-hidden />
        <input
          value={busca}
          maxLength={100}
          aria-label="Buscar nas conversas"
          placeholder="Buscar nas conversas"
          onChange={(event) => setBusca(event.target.value)}
        />
      </label>
      <div className={styles.lista}>
        {conversas.isLoading ? <Spinner /> : null}
        {conversas.isError ? <p className={styles.vazio}>Não foi possível carregar as conversas.</p> : null}
        {conversas.data && conversas.data.length === 0 ? (
          <p className={styles.vazio}>{termo ? 'Nada encontrado.' : 'Nenhuma conversa ainda.'}</p>
        ) : null}
        {conversas.data?.map((conversa) =>
          renomeando?.id === conversa.id ? (
            <form
              key={conversa.id}
              className={styles.renomear}
              onSubmit={(event) => {
                event.preventDefault();
                const titulo = renomeando.titulo.trim();
                if (titulo) atualizar.mutate({ id: conversa.id, mudanca: { titulo } });
                setRenomeando(null);
              }}
            >
              <input
                autoFocus
                value={renomeando.titulo}
                maxLength={120}
                aria-label="Novo nome da conversa"
                onChange={(event) => setRenomeando({ id: conversa.id, titulo: event.target.value })}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setRenomeando(null);
                }}
              />
              <Button type="submit" size="sm">Salvar</Button>
            </form>
          ) : (
            <div
              key={conversa.id}
              className={conversa.id === idAtual ? `${styles.item} ${styles.atual}` : styles.item}
            >
              <button type="button" className={styles.abrir} onClick={() => onAbrir(conversa)}>
                <span className={styles.titulo}>
                  {conversa.fixada ? <Pin size={12} className={styles.pino} aria-label="Fixada" /> : null}
                  {conversa.titulo || 'Conversa sem título'}
                </span>
                <span className={styles.quando}>{quando(conversa.ultimaMensagemEm)}</span>
              </button>
              <ActionMenu
                portal
                ariaLabel={`Opções da conversa ${conversa.titulo ?? ''}`.trim()}
                items={[
                  {
                    id: 'fixar',
                    label: conversa.fixada ? 'Desafixar' : 'Fixar no topo',
                    onClick: () => atualizar.mutate({ id: conversa.id, mudanca: { fixada: !conversa.fixada } }),
                  },
                  {
                    id: 'renomear',
                    label: 'Renomear',
                    onClick: () => setRenomeando({ id: conversa.id, titulo: conversa.titulo ?? '' }),
                  },
                  {
                    id: 'arquivar',
                    label: 'Arquivar',
                    danger: true,
                    onClick: () => atualizar.mutate({ id: conversa.id, mudanca: { arquivada: true } }),
                  },
                ]}
              />
            </div>
          ),
        )}
      </div>
    </div>
  );
}
